/**
 * Auth context — manages Supabase session lifecycle.
 *
 * Restores session on mount, listens to auth state changes,
 * and extracts custom JWT claims (role, customer_id, trip_scope)
 * from app_metadata (server-set, cannot be spoofed by client).
 */
import {
  createContext,
  useContext,
  useEffect,
  useState,
  useCallback,
  type ReactNode,
} from 'react';
import type { Session, User } from '@supabase/supabase-js';
import type { Role } from '@pazportal/types';
import { supabase } from '../lib/supabase';

interface AuthState {
  user: User | null;
  session: Session | null;
  loading: boolean;
  role: Role | null;
  customerId: string | null;
  tripScope: string[] | null;
  signOut: () => Promise<void>;
}

const AuthContext = createContext<AuthState | undefined>(undefined);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [session, setSession] = useState<Session | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // 1. Restore existing session on mount
    supabase.auth.getSession().then(({ data: { session: s } }: { data: { session: Session | null } }) => {
      setSession(s);
      setLoading(false);
    });

    // 2. Listen for auth state changes (login, logout, token refresh)
    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange((_event: string, s: Session | null) => {
      setSession(s);
      setLoading(false);
    });

    return () => subscription.unsubscribe();
  }, []);

  const signOut = useCallback(async () => {
    await supabase.auth.signOut();
    setSession(null);
    // ProtectedRoute handles redirect to /login when session is null
  }, []);

  // Extract custom claims from JWT app_metadata (set server-side, tamper-proof)
  const user = session?.user ?? null;
  const meta = user?.app_metadata ?? {};
  const role = (meta.role as Role) ?? null;
  const customerId = (meta.customer_id as string) ?? null;
  const tripScope = (meta.trip_scope as string[]) ?? null;

  return (
    <AuthContext.Provider
      value={{ user, session, loading, role, customerId, tripScope, signOut }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth(): AuthState {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within an AuthProvider');
  return ctx;
}
